<script lang="ts" setup>
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";

export interface UiDataTreePropsBase {
    hideEmpty?: boolean;
    placement?:
        | "baseline"
        | "top"
        | "middle"
        | "bottom"
        | "text-top"
        | "text-bottom"
        | "sub"
        | "super";
    labelClass?: string;
    valueClass?: string;
    labelTextClass?: string;
    valueTextClass?: string;
    transformLabel?: (label: string) => string;
    size?: "sm" | "md";
}

export interface UiDataTreeProps extends UiDataTreePropsBase {
    sub?: UiDataTreePropsBase;
}

const props = defineProps<UiDataTreeProps>();
const items = defineModel<any>("items", { default: () => [] });

const innerPlacement = computed(() => {
    return props.placement
        ? {
              baseline: "align-baseline",
              top: "align-top",
              middle: "align-middle",
              bottom: "align-bottom",
              "text-top": "align-text-top",
              "text-bottom": "align-text-bottom",
              sub: "align-sub",
              super: "align-super",
          }[props.placement]
        : undefined;
});

const rows = computed(() => {
    let rows: {
        label: { class: string; value: string };
        value: { class: string; value: any };
    }[] = [];

    if (items.value && typeof items.value === "object") {
        rows = Object.entries(items.value).map(([label, value]) => ({
            label: {
                value: props.transformLabel
                    ? props.transformLabel(label)
                    : label,
                class: props.labelClass ?? "",
            },
            value: {
                value:
                    Array.isArray(value) && value.length === 1
                        ? value[0]
                        : value,
                class: props.valueClass ?? "",
            },
        }));
    }

    if (props.hideEmpty) {
        rows = rows.filter((item) => {
            return !(
                !item.value ||
                !item.value?.value ||
                (typeof item.value?.value === "string" &&
                    !item.value?.value.trim()) ||
                (typeof item.value?.value === "object" &&
                    !Object.entries(item.value.value).filter(([k, v]) => v)
                        .length)
            );
        });
    }

    if (innerPlacement.value) {
        rows = rows.map((item) => {
            item.label.class += ` ${innerPlacement.value}`;
            item.value.class += ` ${innerPlacement.value}`;
            item.hasObject = typeof item.value.value === "object";
            return item;
        });
    }

    return rows;
});
</script>

<template>
    <div
        class="flex flex-col"
        v-for="(item, index) in rows"
        :key="item.label.value"
    >
        <UAccordion
            v-if="item.hasObject"
            :items="[item]"
            :ui="{
                item: {
                    base: 'border-l border-hd-neutral-300 pl-4',
                },
            }"
        >
            <template #default="{ open }">
                <UButton
                    variant="link"
                    :padded="false"
                    color="black"
                    class="flex items-center gap-2"
                    :ui="{
                        base: 'transition duration-150 ease-in-out transform active:scale-100 focus:outline-none focus-visible:outline-0 disabled:cursor-not-allowed disabled:opacity-75 flex-shrink-0',
                    }"
                >
                    <template #trailing>
                        <FontAwesomeIcon
                            :icon="faChevronRight"
                            size="xs"
                            class="transform transition duration-75 ease-in-out"
                            :class="{
                                'rotate-90': open,
                            }"
                        />
                    </template>
                    <span
                        class="font-medium text-gray-900"
                        :class="[
                            labelTextClass,
                            size === 'sm'
                                ? 'text-sm leading-6'
                                : 'text-base leading-7',
                        ]"
                        >{{ item.label.value }}</span
                    >
                </UButton>
            </template>
            <template #item>
                <UiDataTree
                    :items="item.value.value"
                    :hide-empty="sub?.hideEmpty ?? hideEmpty"
                    :placement="sub?.placement ?? placement"
                    :labelClass="sub?.labelClass ?? labelClass"
                    :valueClass="sub?.valueClass ?? valueClass"
                    :size="sub?.size ?? size"
                    :labelTextClass="sub?.labelTextClass ?? labelTextClass"
                    :valueTextClass="sub?.valueTextClass ?? valueTextClass"
                    :transform-label="sub?.transformLabel ?? transformLabel"
                />
            </template>
        </UAccordion>
        <div v-else class="flex gap-1 items-start">
            <span
                class="font-medium text-gray-900"
                :class="[
                    labelTextClass,
                    size === 'sm' ? 'text-sm leading-6' : 'text-base leading-7',
                ]"
                >{{ item.label.value }}:</span
            >
            <ClientOnly>
                <UiDataItem
                    :size="size ?? 'md'"
                    :item-class="valueTextClass"
                    :model-value="item.value.value"
                />
            </ClientOnly>
        </div>
    </div>
</template>
